import { useEffect, useRef, type ReactNode } from "react"
import type { Participant } from "@vc/models"
import { tilesThatFit } from "@/features/meeting/services/grid-order"

interface MobileStageLayoutProps {
  /** What is being presented — a shared screen, document or video. */
  stage: ReactNode
  participants: Participant[]
  renderTile: (participant: Participant) => ReactNode
  isLandscape: boolean
  /**
   * How many tiles the strip shows without scrolling, on mount and whenever it
   * resizes or turns. The strip scrolls, so "on screen" is not the whole list:
   * a portrait phone fits about two and a half. MeetingScreen seats the active
   * speaker within this many (BUG-000658), as the desktop rail does within its
   * page of six.
   */
  onVisibleCountChange?: (count: number) => void
}

/**
 * The phone layout for anything presented: the content takes the screen, and
 * everyone else is a strip of small tiles that scrolls — the way Meet and Zoom
 * lay out a presentation on a phone.
 *
 * It replaces two layouts that did not fit a phone. The document and video
 * shares rendered the DESKTOP layout — main area plus a fixed-width rail of
 * tiles on the right — so on a 390px screen the document was squeezed into a
 * sliver and cut off, and the tiles sat beside it. The mobile screen share gave
 * the screen 40% of the height and a grid the rest, collapsing everyone past
 * three into an "N others" tile.
 *
 * Portrait: content on top, a horizontal strip below. Landscape: content on
 * the left, a vertical strip on the right. Every participant stays reachable
 * by scrolling; nobody is folded into a count.
 */
export function MobileStageLayout({
  stage,
  participants,
  renderTile,
  isLandscape,
  onVisibleCountChange,
}: MobileStageLayoutProps) {
  const stripRef = useRef<HTMLDivElement>(null)
  const hasTiles = participants.length > 0

  useEffect(() => {
    const strip = stripRef.current
    if (!strip || !onVisibleCountChange) return
    // Measured off the first tile rather than restated from the classes
    // below, so a change to the tile size cannot leave this counting wrong.
    const measure = () => {
      const tile = strip.firstElementChild as HTMLElement | null
      if (!tile) return
      const gap = parseFloat(getComputedStyle(strip).gap) || 0
      onVisibleCountChange(
        isLandscape
          ? tilesThatFit(strip.clientHeight, tile.offsetHeight, gap)
          : tilesThatFit(strip.clientWidth, tile.offsetWidth, gap),
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(strip)
    return () => observer.disconnect()
  }, [isLandscape, hasTiles, onVisibleCountChange])

  if (isLandscape) {
    return (
      <div className="flex h-full w-full min-w-0 flex-row gap-2">
        <div className="relative h-full min-w-0 flex-1 overflow-hidden rounded-xl">{stage}</div>
        {participants.length > 0 && (
          <div
            ref={stripRef}
            data-testid="mobile-participant-strip"
            className="flex h-full w-[132px] shrink-0 flex-col gap-2 overflow-y-auto overscroll-contain"
          >
            {participants.map((p) => (
              <div key={p.id} className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg">
                {renderTile(p)}
              </div>
            ))}
            {/* Room to scroll the last tile out from under the floating
                control-bar button, which sits over this corner. */}
            <div aria-hidden className="h-16 w-full shrink-0" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-2">
      <div className="relative min-h-0 w-full flex-1 overflow-hidden rounded-xl">{stage}</div>
      {participants.length > 0 && (
        <div
          ref={stripRef}
          data-testid="mobile-participant-strip"
          className="flex h-[104px] w-full shrink-0 snap-x flex-row gap-2 overflow-x-auto overscroll-contain"
        >
          {participants.map((p) => (
            <div key={p.id} className="relative h-full aspect-[4/3] shrink-0 snap-start overflow-hidden rounded-lg">
              {renderTile(p)}
            </div>
          ))}
          {/* As above, for the floating button at the strip's end. */}
          <div aria-hidden className="h-full w-16 shrink-0" />
        </div>
      )}
    </div>
  )
}
